import React, { useCallback, useEffect, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { Send } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/contexts/Auth0Context'
import { curatedItemCommentService } from '@/services/curatedItemCommentService'
import type { CuratedItemComment } from '@/services/curatedItemCommentService'

const PAGE_SIZE = 20

interface CuratedCommentThreadProps {
  curatedItemId: string
  /** Notifies the parent when the on-screen comment count changes. */
  onCountChange?: (count: number) => void
}

const CuratedCommentThread: React.FC<CuratedCommentThreadProps> = ({ curatedItemId, onCountChange }) => {
  const { user } = useAuth()

  const [comments, setComments] = useState<CuratedItemComment[]>([])
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [draft, setDraft] = useState('')
  const [posting, setPosting] = useState(false)

  const loadMore = useCallback(async (resetCursor?: string): Promise<void> => {
    setLoading(true)
    try {
      const page = await curatedItemCommentService.getComments(curatedItemId, {
        cursor: resetCursor,
        limit: PAGE_SIZE
      })
      setComments((prev) => (resetCursor === undefined ? page.items : [...prev, ...page.items]))
      setHasMore(page.hasMore)
      setCursor(page.nextCursor)
    } finally {
      setLoading(false)
    }
  }, [curatedItemId])

  useEffect(() => {
    void loadMore(undefined)
  }, [loadMore])

  const submitComment = async (): Promise<void> => {
    const text = draft.trim()
    if (!text || posting) return
    setPosting(true)
    try {
      const created = await curatedItemCommentService.createComment(curatedItemId, text)
      setComments((prev) => [...prev, created])
      setDraft('')
      onCountChange?.(comments.length + 1)
    } finally {
      setPosting(false)
    }
  }

  const remove = async (commentId: string): Promise<void> => {
    await curatedItemCommentService.deleteComment(commentId)
    setComments((prev) => {
      const next = prev.filter((c) => c.id !== commentId)
      onCountChange?.(next.length)
      return next
    })
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {comments.map((c) => (
          <div key={c.id} className="rounded-md bg-gray-50 px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-gray-800">
                {c.author.firstName} {c.author.lastName}
              </span>
              <span className="text-xs text-gray-400">
                {formatDistanceToNow(new Date(c.createdAt), { addSuffix: true })}
              </span>
            </div>
            <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{c.content}</p>
            {(c.authorId === user?.id || user?.role === 'admin') && (
              <button
                type="button"
                onClick={() => remove(c.id)}
                className="mt-1 text-xs text-red-600 hover:underline"
              >
                Delete
              </button>
            )}
          </div>
        ))}
        {comments.length === 0 && !loading && (
          <p className="text-sm text-gray-400">No comments yet.</p>
        )}
        {hasMore && (
          <button
            type="button"
            onClick={() => void loadMore(cursor)}
            className="text-sm text-primary-600 hover:underline disabled:opacity-50"
            disabled={loading}
          >
            Load more comments
          </button>
        )}
      </div>

      <div className="flex items-start gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          placeholder="Add a comment"
          className="flex-1 resize-none rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        <Button
          type="button"
          size="sm"
          onClick={submitComment}
          isLoading={posting}
          disabled={draft.trim().length === 0}
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

export default CuratedCommentThread
